import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input, Select, Button, Card } from '@brainsway/ui';
import { Save, Send } from 'lucide-react';
import { useTreatmentTypes, useHelmetTypes } from '../hooks';
import type { Report, ReportFormData, Hospital } from '../types';

const reportSchema = z.object({
  reportTypeCode: z.string().min(1),
  hospitalId: z.string().min(1, 'Hospital is required'),
  date: z.string().min(1, 'Date is required'),
  startTime: z.string().min(1, 'Start time is required'),
  endTime: z.string().min(1, 'End time is required'),
  treatmentType: z.string().optional(),
  helmetType: z.string().optional(),
  quantity: z.coerce.number().int().min(1).default(1),
  description: z.string().optional(),
  notes: z.string().default(''),
  status: z.enum(['DRAFT', 'SUBMITTED']),
}).refine((data) => {
  if (data.reportTypeCode === 'TREATMENT') {
    return !!data.treatmentType && !!data.helmetType;
  }
  return true;
}, {
  message: 'Treatment type and helmet type are required for Treatment Reporting',
  path: ['treatmentType'],
});

type FormValues = z.infer<typeof reportSchema>;

interface ReportFormProps {
  hospitals: Hospital[];
  existingReport?: Report;
  onSubmit: (data: ReportFormData) => void;
  isLoading?: boolean;
}

export function ReportForm({ hospitals, existingReport, onSubmit, isLoading }: ReportFormProps) {
  const { t } = useTranslation();
  const { data: treatmentTypesData } = useTreatmentTypes({ isActive: 'true' });
  const { data: helmetTypesData } = useHelmetTypes({ isActive: 'true' });

  const treatmentTypes = treatmentTypesData?.treatmentTypes ?? [];
  const helmetTypes = helmetTypesData?.helmetTypes ?? [];

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(reportSchema),
    defaultValues: {
      reportTypeCode: existingReport?.reportTypeCode || 'TREATMENT',
      hospitalId: existingReport?.hospitalId || '',
      date: existingReport?.date || new Date().toISOString().split('T')[0],
      startTime: existingReport?.startTime || '',
      endTime: existingReport?.endTime || '',
      treatmentType: existingReport?.treatmentType || '',
      helmetType: existingReport?.helmetType || '',
      quantity: existingReport?.quantity || 1,
      description: existingReport?.description || '',
      notes: existingReport?.notes || '',
      status: 'DRAFT',
    },
  });

  const watchedReportType = watch('reportTypeCode');

  // Clear treatment-specific fields when switching to TRAINING
  useEffect(() => {
    if (watchedReportType === 'TRAINING') {
      setValue('treatmentType', '');
      setValue('helmetType', '');
    }
  }, [watchedReportType, setValue]);

  const handleFormSubmit = (status: 'DRAFT' | 'SUBMITTED') => {
    setValue('status', status);
    handleSubmit((data) => onSubmit(data as ReportFormData))();
  };

  const hospitalOptions = [
    { value: '', label: t('reports.selectHospital', 'Select hospital') },
    ...hospitals.map((h) => ({ value: h.id, label: h.name })),
  ];

  return (
    <Card className="glass-surface">
      <form className="space-y-4 p-5">
        {/* Report Type */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setValue('reportTypeCode', 'TREATMENT')}
            className={`flex-1 rounded-lg border border-white/30 px-4 py-2 text-sm font-bold transition-colors ${watchedReportType === 'TREATMENT' ? 'bg-primary-500 text-white' : 'glass-surface text-neutral-700'
              }`}
          >
            {t('reports.treatment', 'Treatment')}
          </button>
          <button
            type="button"
            onClick={() => setValue('reportTypeCode', 'TRAINING')}
            className={`flex-1 rounded-lg border border-white/30 px-4 py-2 text-sm font-bold transition-colors ${watchedReportType === 'TRAINING' ? 'bg-accent-500 text-white' : 'glass-surface text-neutral-700'
              }`}
          >
            {t('reports.teaching', 'Training')}
          </button>
        </div>

        <Select
          label={t('reports.hospital')}
          options={hospitalOptions}
          error={errors.hospitalId?.message}
          {...register('hospitalId')}
        />

        <Input
          label={t('reports.date')}
          type="date"
          error={errors.date?.message}
          {...register('date')}
        />

        {/* Treatment-specific fields */}
        {watchedReportType === 'TREATMENT' && (
          <>
            <Select
              label={t('reports.treatmentType')}
              options={[
                { value: '', label: t('reports.selectTreatmentType', 'Select type') },
                ...treatmentTypes.map((tt) => ({ value: tt.code, label: tt.name })),
              ]}
              error={errors.treatmentType?.message}
              {...register('treatmentType')}
            />
            <Select
              label={t('reports.helmetType')}
              options={[
                { value: '', label: t('reports.selectHelmetType', 'Select helmet') },
                ...helmetTypes.map((ht) => ({ value: ht.code, label: ht.name })),
              ]}
              error={errors.helmetType?.message}
              {...register('helmetType')}
            />
            <Input
              label={t('reports.quantity', 'Quantity')}
              type="number"
              min={1}
              error={errors.quantity?.message}
              {...register('quantity')}
            />
          </>
        )}

        {/* Training-specific fields */}
        {watchedReportType === 'TRAINING' && (
          <Input
            label={t('teaching.topic', 'Topic / Description')}
            error={errors.description?.message}
            {...register('description')}
          />
        )}

        <div className="grid grid-cols-2 gap-4">
          <Input
            label={t('reports.startTime')}
            type="time"
            error={errors.startTime?.message}
            {...register('startTime')}
          />
          <Input
            label={t('reports.endTime')}
            type="time"
            error={errors.endTime?.message}
            {...register('endTime')}
          />
        </div>

        <Input
          label={t('reports.notes')}
          error={errors.notes?.message}
          {...register('notes')}
        />

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleFormSubmit('DRAFT')}
            loading={isLoading}
            className="flex-1 gap-1.5"
          >
            <Save className="h-4 w-4" />
            {t('reports.saveDraft', 'Save Draft')}
          </Button>
          <Button
            type="button"
            onClick={() => handleFormSubmit('SUBMITTED')}
            loading={isLoading}
            className="flex-1 gap-1.5"
          >
            <Send className="h-4 w-4" />
            {t('reports.submit', 'Submit')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
